/* [BD feat] bd-product-ugc.js */
(function() {
  'use strict';

  class UGCStoryViewer {
    constructor(data, modalId) {
      this.data = data;
      this.currentIndex = 0;
      this.modal = document.getElementById(modalId);
      
      if (!this.modal) {
        console.error('[BD UGC] Modal not found:', modalId);
        return;
      }
      
      this.isPlaying = false;
      this.duration = 5000;
      
      // ✅ Null-guard all DOM queries
      this.mediaContainer = this.modal.querySelector('.bd-ugc-modal__media-container');
      this.mediaStage = this.modal.querySelector('.bd-ugc-modal__media-stage');
      this.progressContainer = this.modal.querySelector('.bd-ugc-modal__progress-container');
      this.playBtn = this.modal.querySelector('.bd-ugc-modal__play-btn');
      
      if (!this.mediaContainer || !this.mediaStage || !this.progressContainer || !this.playBtn) {
        console.error('[BD UGC] Required modal elements not found');
        return;
      }
      
      this.userInfo = {
        social: this.modal.querySelector('.bd-ugc-modal__social-icon-img'),
        avatar: this.modal.querySelector('.bd-ugc-modal__user-avatar'),
        name: this.modal.querySelector('.bd-ugc-modal__username')
      };

      // Bind keyboard handler with proper scoping
      this.handleKeydown = this.handleKeydown.bind(this);

      this.initProgressBars();
      this.bindEvents();
    }

    initProgressBars() {
      if (!this.progressContainer) return;
      
      this.progressContainer.innerHTML = '';
      this.data.forEach((_, i) => {
        const bar = document.createElement('div');
        bar.className = 'bd-ugc-progress-bar';
        bar.innerHTML = `<div class="bd-ugc-progress-fill" id="progress-${i}"></div>`;
        this.progressContainer.appendChild(bar);
      });
    }

    bindEvents() {
      const closeBtn = this.modal.querySelector('.bd-ugc-modal__close');
      const backdrop = this.modal.querySelector('.bd-ugc-modal__backdrop');
      const navLeft = this.modal.querySelector('.bd-ugc-modal__nav-left');
      const navRight = this.modal.querySelector('.bd-ugc-modal__nav-right');

      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }
      
      if (backdrop) {
        backdrop.addEventListener('click', () => this.close());
      }

      if (navLeft) {
        navLeft.addEventListener('click', (e) => {
          e.stopPropagation();
          this.prev();
        });
      }

      if (navRight) {
        navRight.addEventListener('click', (e) => {
          e.stopPropagation();
          this.next();
        });
      }

      if (this.playBtn) {
        this.playBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.togglePlay();
        });
      }

      if (this.mediaStage) {
        this.mediaStage.addEventListener('click', () => this.togglePlay());
      }
    }

    // ✅ Separate keyboard handler that can be added/removed
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
      }
    }

    open(index) {
      this.currentIndex = index;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // ✅ Add keyboard listener only when modal is open
      document.addEventListener('keydown', this.handleKeydown);
      
      this.loadSlide(this.currentIndex);
    }

    close() {
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // ✅ Remove keyboard listener when modal closes
      document.removeEventListener('keydown', this.handleKeydown);
      
      this.stopCurrent();
    }

    stopCurrent() {
      const video = this.mediaStage?.querySelector('video');
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
      cancelAnimationFrame(this.rafId);
      this.isPlaying = false;
    }

    loadSlide(index) {
      this.stopCurrent();
      this.currentIndex = index;
      const item = this.data[index];

      // Update user info with null guards
      if (this.userInfo.name) {
        this.userInfo.name.textContent = item.username || '';
      }
      if (this.userInfo.avatar && item.avatar) {
        this.userInfo.avatar.src = item.avatar;
        this.userInfo.avatar.style.opacity = '1';
      } else {
        this.userInfo.avatar.style.opacity = '0';
      }
      if (this.userInfo.social && item.social) {
        this.userInfo.social.src = item.social;
        this.userInfo.social.style.opacity = '1';
      } else {
        this.userInfo.social.style.opacity = '0';
      }

      // Update progress bars
      this.data.forEach((_, i) => {
        const fill = document.getElementById(`progress-${i}`);
        if (fill) {
          fill.style.width = i < index ? '100%' : '0%';
        }
      });

      // Clear media stage
      if (this.mediaStage) {
        this.mediaStage.innerHTML = '';
      }
      
      if (this.mediaContainer) {
        this.mediaContainer.classList.add('is-loading');
      }
      
      if (this.playBtn) {
        this.playBtn.classList.add('is-hidden');
      }

      if (item.type === 'video') {
        const vid = document.createElement('video');
        vid.src = item.src;
        vid.poster = item.poster;
        vid.className = 'bd-ugc-modal__media-item';
        vid.playsInline = true;
        vid.muted = true; // ✅ Must be true for autoplay to work on iOS/mobile

        vid.addEventListener('loadeddata', () => {
          if (this.mediaContainer) {
            this.mediaContainer.classList.remove('is-loading');
          }
          this.playVideo(vid, index);
        });
        
        vid.addEventListener('ended', () => {
          this.next();
        });
        
        if (this.mediaStage) {
          this.mediaStage.appendChild(vid);
        }
        this.currentMedia = vid;

      } else {
        // Image
        const img = document.createElement('img');
        img.src = item.poster || item.src;
        img.className = 'bd-ugc-modal__media-item';
        
        img.addEventListener('load', () => {
          if (this.mediaContainer) {
            this.mediaContainer.classList.remove('is-loading');
          }
          this.startImageTimer(index);
        });
        
        img.addEventListener('error', () => {
          console.error('[BD UGC] Image failed to load:', item.poster || item.src);
          if (this.mediaContainer) {
            this.mediaContainer.classList.remove('is-loading');
          }
        });
        
        if (this.mediaStage) {
          this.mediaStage.appendChild(img);
        }
        this.currentMedia = img;
      }
    }

    playVideo(video, index) {
      video.play()
        .then(() => {
          this.isPlaying = true;
          if (this.playBtn) {
            this.playBtn.classList.add('is-hidden');
          }
          this.startProgressLoop(video, index);
        })
        .catch(() => {
          // Autoplay blocked - show play button
          this.isPlaying = false;
          if (this.playBtn) {
            this.playBtn.classList.remove('is-hidden');
          }
        });
    }

    togglePlay() {
      if (!this.currentMedia || this.data[this.currentIndex].type !== 'video') return;
      
      if (this.currentMedia.paused) {
        this.currentMedia.play();
        this.isPlaying = true;
        if (this.playBtn) {
          this.playBtn.classList.add('is-hidden');
        }
        this.startProgressLoop(this.currentMedia, this.currentIndex);
      } else {
        this.currentMedia.pause();
        this.isPlaying = false;
        if (this.playBtn) {
          this.playBtn.classList.remove('is-hidden');
        }
        cancelAnimationFrame(this.rafId);
      }
    }

    startProgressLoop(video, index) {
      const fill = document.getElementById(`progress-${index}`);
      
      const step = () => {
        if (!this.isPlaying || this.currentIndex !== index) return;
        
        if (video.duration && fill) {
          const pct = (video.currentTime / video.duration) * 100;
          fill.style.width = `${pct}%`;
        }
        
        if (!video.paused && !video.ended) {
          this.rafId = requestAnimationFrame(step);
        }
      };
      
      this.rafId = requestAnimationFrame(step);
    }

    startImageTimer(index) {
      const fill = document.getElementById(`progress-${index}`);
      const start = performance.now();
      this.isPlaying = true;

      const step = (now) => {
        if (!this.isPlaying || this.currentIndex !== index) return;
        
        const elapsed = now - start;
        const pct = Math.min((elapsed / this.duration) * 100, 100);
        
        if (fill) {
          fill.style.width = `${pct}%`;
        }

        if (elapsed < this.duration) {
          this.rafId = requestAnimationFrame(step);
        } else {
          this.next();
        }
      };
      
      this.rafId = requestAnimationFrame(step);
    }

    next() {
      const nextIndex = this.currentIndex < this.data.length - 1 
        ? this.currentIndex + 1 
        : 0; // Loop back to first
      this.loadSlide(nextIndex);
    }

    prev() {
      // ✅ Go to last slide when at beginning (Instagram behavior)
      const prevIndex = this.currentIndex > 0 
        ? this.currentIndex - 1 
        : this.data.length - 1;
      this.loadSlide(prevIndex);
    }
  }

  const initUGC = () => {
    const section = document.querySelector('.bd-ugc');
    if (!section) return;
    
    const sectionId = section.dataset.sectionId;

    // Initialize Swiper
    const swiperEl = document.getElementById(`bd-ugc-swiper-${sectionId}`);
    if (swiperEl && typeof Swiper !== 'undefined') {
      new Swiper(swiperEl, {
        loop: true,
        slidesPerView: 1.8,
        spaceBetween: 10,
        breakpoints: {
          750: {
            slidesPerView: 2.5,
            centeredSlides: true,
            spaceBetween: 9
          }
        }
      });
    }

    // Initialize Story Viewer
    const dataEl = document.getElementById(`bd-ugc-data-${sectionId}`);
    if (dataEl) {
      let storyData = null;
      
      try {
        // ✅ Safely parse JSON with error handling
        const jsonText = dataEl.textContent || dataEl.innerText;
        storyData = JSON.parse(jsonText);
      } catch (error) {
        console.error('[BD UGC] Failed to parse story data:', error);
        return;
      }

      // ✅ Null-guard: ensure data exists and is an array
      if (!storyData || !Array.isArray(storyData) || storyData.length === 0) {
        console.warn('[BD UGC] No valid story data found');
        return;
      }

      const storyViewer = new UGCStoryViewer(storyData, `bd-ugc-modal-${sectionId}`);
      
      // Bind card triggers
      const cards = section.querySelectorAll('.bd-ugc__card');
      cards.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const index = parseInt(trigger.dataset.index, 10);
          if (!isNaN(index)) {
            storyViewer.open(index);
          }
        });
      });
    }
  };

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUGC);
  } else {
    initUGC();
  }
  
  document.addEventListener('shopify:section:load', initUGC);

})();

/* [BD feat] bd-product-ugc.js */